"use client";

import { cn } from "@/lib/utils";
import { Shield, Heart, Wrench, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";

// Icon order matches the values array order in the JSON
const VALUE_ICONS = [Shield, Heart, Wrench, Users];

interface MissionSectionProps {
  className?: string;
}

export function MissionSection({ className }: MissionSectionProps) {
  const t = useTranslations('mission');
  const values = t.raw('values') as Array<{ title: string; description: string }>;


  return (
    <section
      className={cn(
        "py-20 bg-site-section relative overflow-hidden",
        className
      )}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-32 h-32 border border-yellow-500 rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-24 h-24 border border-yellow-500 rounded-full"></div>
      </div>

      <div className="mx-4 md:mx-16 lg:mx-20 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-yellow-500 text-sm uppercase tracking-wide mb-4">
            <div className="w-8 h-px bg-yellow-500"></div>
            <span>{t('sectionBadge')}</span>
            <div className="w-8 h-px bg-yellow-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-outfit text-white mb-6">
            {t('sectionTitle')} <span className="text-yellow-500">{t('sectionTitleHighlight')}</span>
          </h2>
          <p className="text-white/80 text-lg md:text-xl max-w-3xl mx-auto font-light">
            {t('sectionSubtitle')}
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {values.map((value, index) => {
            const Icon = VALUE_ICONS[index];
            return (
            <div
              key={index}
              className="group text-center p-6 rounded-lg border border-white/10 hover:border-yellow-500/30 transition-all duration-300 hover:bg-white/5"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-yellow-500/10 flex items-center justify-center group-hover:bg-yellow-500/20 transition-colors">
                <Icon className="w-8 h-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 font-outfit">{value.title}</h3>
              <p className="text-white/70 font-light leading-relaxed">{value.description}</p>
            </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="flex justify-center">
            <Image
              src="/images/miller40-mascot.png"
              alt={t('mascotAlt')}
              width={466}
              height={700}
              sizes="(max-width: 768px) 320px, (max-width: 1024px) 420px, 460px"
              className="w-auto h-auto max-w-[320px] md:max-w-[420px] lg:max-w-[460px] object-contain drop-shadow-2xl"
            />
          </div>

          <div className="space-y-8">
            <div className="bg-gradient-to-r from-yellow-500/10 to-transparent rounded-2xl p-8 border-l-4 border-yellow-500">
              <h3 className="text-2xl md:text-3xl font-semibold text-yellow-500 mb-3 font-outfit">
                {t('visionTitle')}
              </h3>
              <div className="w-12 h-1 bg-yellow-500 mb-4"></div>
              <p className="text-white text-lg font-light leading-relaxed">
                {t('visionStatement')}
              </p>
            </div>

            <div className="bg-gradient-to-r from-yellow-500/10 to-transparent rounded-2xl p-8 border-l-4 border-yellow-500">
              <h3 className="text-2xl md:text-3xl font-semibold text-yellow-500 mb-3 font-outfit">
                {t('missionTitle')}
              </h3>
              <div className="w-12 h-1 bg-yellow-500 mb-4"></div>
              <p className="text-white text-lg font-light leading-relaxed">
                {t('missionStatement')}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <h4 className="text-2xl md:text-3xl text-white mb-4 font-outfit">
            {t('ctaTitle')} <span className="text-yellow-500">{t('ctaTitleHighlight')}</span>
          </h4>
          <p className="text-white/70 text-lg">{t('ctaSubtitle')}</p>
        </div>
      </div>
    </section>
  );
}
